import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar, containerVariants, itemVariants } from './dashboard/DashboardCharts';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-4742" />} data-id="element-4741">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-4743">
        <motion.div variants={itemVariants} data-id="element-4744">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-4746" />} data-id="element-4745" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4747">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-4749" />} data-id="element-4748" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4750">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-4752" />} data-id="element-4751" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4753">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-4755" />} data-id="element-4754" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-4756">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4757">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4758">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-4759">
            <div className="flex items-center justify-between" data-id="element-4760">
              <div className="flex items-center gap-2" data-id="element-4761">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-4762" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4763">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-4764">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4765">
              <div className="flex items-center gap-2" data-id="element-4766">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-4767" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4768">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-4769">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-4770" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-4771" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-4772">
            <div className="flex items-center justify-between" data-id="element-4773">
              <div className="flex items-center gap-3" data-id="element-4774">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-4775" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-4776" />}
                <div data-id="element-4777">
                  <div className="text-sm font-medium text-gray-700" data-id="element-4778">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-4779">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-4780">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-4781" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-4782">
                {['Geslaagd', 'Warnings', 'Fouten'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-4783">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-4784" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-4785" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-4786">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-4787">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-4788">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-4789">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-4790">
                    <div className="text-lg font-bold text-amber-700" data-id="element-4791">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-4792">Warnings</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-4793">
                    <div className="text-lg font-bold text-red-700" data-id="element-4794">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-4795">Fouten</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-4796">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4797" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4798" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4799" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-4800">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-4801">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4802">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4803">
            <Clock className="w-4 h-4" data-id="element-4804" /> Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-4805">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-4806">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-4807">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-4808" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-4809" /> : <Activity className="w-4 h-4" data-id="element-4810" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-4811">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-4812">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-4813">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4814">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4815">
            <Server className="w-4 h-4" data-id="element-4816" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-4817">
            <div className="flex items-center justify-between" data-id="element-4818">
              <span className="text-sm text-gray-600" data-id="element-4819">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-4820">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4821">
              <span className="text-sm text-gray-600" data-id="element-4822">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-4823">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-4824">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4825">
                <span className="text-sm text-gray-600" data-id="element-4826">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4827">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4828">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-4829" />
              </div>
            </div>
            <div data-id="element-4830">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4831">
                <span className="text-sm text-gray-600" data-id="element-4832">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4833">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4834">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-4835" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-4836">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4837">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4838">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-4839">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-4840">
                <div className="flex justify-between text-xs mb-1" data-id="element-4841">
                  <span className="text-gray-600" data-id="element-4842">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-4843">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4844">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-4845" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4846">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4847">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-4848" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-4849">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-4850">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-4851">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4852">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-4853">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-4854">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-4855">Geen leerlingen gevonden</p>}
        </motion.div>

        {/* Calibration Progress */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4856">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4857">
            <ShieldCheck className="w-3.5 h-3.5" data-id="element-4858" /> Kalibratie
          </h3>
          <div className="space-y-3" data-id="element-4859">
            <div className="flex items-end justify-between" data-id="element-4860">
              <span className="text-2xl font-bold text-gray-900" data-id="element-4861">
                {contentStats.calibration.goldStandardPct}%
              </span>
              <span className="text-xs text-gray-500" data-id="element-4862">
                {contentStats.calibration.familiesAtGoldStandard} /{' '}
                {contentStats.calibration.totalFamilies} goud
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4863">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${contentStats.calibration.goldStandardPct}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className={`h-full rounded-full ${contentStats.calibration.goldStandardPct >= 80 ? 'bg-emerald-500' : contentStats.calibration.goldStandardPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-4864" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-4865">
              <div className="flex justify-between" data-id="element-4866">
                <span className="text-gray-500" data-id="element-4867">citoDifficulty</span>
                <span className="font-semibold text-gray-700" data-id="element-4868">
                  {contentStats.calibration.citoDifficultyCoveragePct}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4869">
                <span className="text-gray-500" data-id="element-4870">solutionSteps</span>
                <span className="font-semibold text-gray-700" data-id="element-4871">
                  {contentStats.calibration.solutionStepsCoveragePct}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageLayout>;
}